import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import TelemetryDeck from '@telemetrydeck/sdk';
import * as Sentry from '@sentry/angular';
import { environment } from 'src/environments/environment';
import { IEmployee } from '../models/employee.model';
import { AuthService } from './auth.service';

type TTelemetrySignalType = 'Navigation.PageView';
type TTelemetrySignalPayload = Readonly<Record<string, string>>;

@Injectable({
  providedIn: 'root',
})
export class TelemetryDeckService {
  private readonly isEnabled: boolean = environment.telemetryDeck.enabled && !!environment.telemetryDeck.appId;
  private telemetryDeck?: TelemetryDeck;
  private isRouteTrackingInitialized: boolean = false;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
  ) {}

  public initialize(): void {
    if (this.isEnabled && !this.isRouteTrackingInitialized) {
      this.trackRouteChanges();
      this.isRouteTrackingInitialized = true;
    }

    // Try to track current route immediately (signal will be skipped if not logged in).
    this.trackPageView(this.router.url);
  }

  private trackRouteChanges(): void {
    this.router.events.subscribe((event) => {
      // Track only completed navigations.
      if (event instanceof NavigationEnd) {
        this.trackPageView(event.urlAfterRedirects || event.url);
      }
    });
  }

  private trackPageView(route: string): void {
    this.sendSignal('Navigation.PageView', { route: route.split('?')[0] });
  }

  private getClientUserIdentifier(): string | null {
    const employee: IEmployee | null = this.authService.getCurrentEmployee();

    // Internal analytics should only use authenticated employee ids.
    if (employee?._id) {
      return employee._id;
    } else {
      return null;
    }
  }

  private sendSignal(type: TTelemetrySignalType, payload?: TTelemetrySignalPayload): void {
    const clientUserIdentifier = this.getClientUserIdentifier();

    // Skip when telemetry is disabled or no authenticated user is present.
    if (!this.isEnabled || !clientUserIdentifier) {
    } else {
      // Lazy SDK initialization once user context is available.
      if (!this.telemetryDeck) {
        this.telemetryDeck = new TelemetryDeck({
          appID: environment.telemetryDeck.appId,
          clientUser: clientUserIdentifier,
        });
      }

      // Keep clientUser in sync in case auth context changes.
      this.telemetryDeck.clientUser = clientUserIdentifier;

      void this.telemetryDeck.signal(type, payload).catch((error: unknown) => {
        Sentry.captureException(error);
      });
    }
  }
}
